import React, { useEffect, useState } from "react";
import axios from "axios";
import { LoaderType, Loading } from "@/layouts/buddy/loading";
import { KeenIcon } from "@/components";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useStatistic } from "@/contexts/StatisticContext.tsx";

// Define a unified interface to represent any entity.
interface Entity {
    p_id: string;
    name: string;
}

interface UploadTextProps {
    /**
     * The parent entity this upload relates to (candidate, job, or company), if any.
     * For job uploads in create mode, parentEntity is required.
     */
    parentEntity?: Entity | null;
    /**
     * The object entity this upload targets (the actual entity to which text is added).
     * For addSource mode, this becomes essential.
     */
    objectEntity?: Entity | null;
    /** The type of entity this upload relates to: "candidate", "job", or "company". */
    entityType: "candidate" | "job" | "company";
    /**
     * The mode affects behavior or endpoint selection:
     *   - "create"    for creating a new entity,
     *   - "addSource" for adding additional info (source) to an existing entity.
     */
    mode: "create" | "addSource";
    /** Optional callback to trigger on a successful submission. */
    onSuccess?: () => void;
}

const UploadText: React.FC<UploadTextProps> = ({
                                                   parentEntity = null,
                                                   objectEntity = null,
                                                   entityType,
                                                   mode,
                                                   onSuccess,
                                               }) => {
    const [loading, setLoading] = useState(false);
    const [text, setText] = useState<string>("");
    const navigate = useNavigate();
    const { refreshStatistic } = useStatistic();

    // Map endpoints for text uploads.
    const apiUrlMap = {
        candidate: {
            create: `${import.meta.env.VITE_APP_API_URL}/candidate/create/text`,
            addSource: `${import.meta.env.VITE_APP_API_URL}/candidate/add-source/text`,
        },
        job: {
            create: `${import.meta.env.VITE_APP_API_URL}/job/create/text`,
            addSource: `${import.meta.env.VITE_APP_API_URL}/job/add-source/text`,
        },
        company: {
            create: `${import.meta.env.VITE_APP_API_URL}/company/create/text`,
            addSource: `${import.meta.env.VITE_APP_API_URL}/company/add-source/text`,
        },
    };

    // Derive the correct base endpoint from entityType & mode.
    const API_TEXT_UPLOAD = apiUrlMap[entityType][mode];

    // Determine the final upload URL.
    // For create mode, use the parent entity's p_id (if provided) so that the new object is linked to its parent.
    // For addSource mode, use the object entity's p_id so that the API knows which object is receiving the new source.
    let uploadUrl = API_TEXT_UPLOAD;
    if (mode === "create" && parentEntity?.p_id && entityType !== "company") {
        uploadUrl = `${API_TEXT_UPLOAD}/${parentEntity.p_id}`;
    } else if (mode === "addSource" && objectEntity?.p_id) {
        uploadUrl = `${API_TEXT_UPLOAD}/${objectEntity.p_id}`;
    }

    // Validate required entities based on the mode.
    useEffect(() => {
        if (mode === "create" && entityType === "job" && !parentEntity?.p_id) {
            enqueueSnackbar("Please select the parent company.", {
                variant: "solid",
                state: "danger",
            });
        }
        if (mode === "addSource" && !objectEntity?.p_id) {
            enqueueSnackbar("Please select the object entity.", {
                variant: "solid",
                state: "danger",
            });
        }
    }, [parentEntity, objectEntity, entityType, mode]);

    const handleSubmit = async () => {
        // For job creation, ensure a valid parent is provided.
        if (mode === "create" && entityType === "job" && !parentEntity?.p_id) {
            enqueueSnackbar("Please select the parent company before submitting text.", {
                variant: "solid",
                state: "danger",
            });
            return;
        }

        // Basic input validation.
        if (text.trim().length < 10) {
            enqueueSnackbar("Please provide at least 10 characters of text input.", {
                variant: "solid",
                state: "danger",
            });
            return;
        }

        setLoading(true);

        try {
            // POST the text to the backend.
            const response = await axios.post(
                uploadUrl,
                { text },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            // Construct the path to the newly created or updated entity.
            const newPath = `/${entityType}/${response.data.p_id}`;

            // If we are already on that path, just refresh. Otherwise, navigate to it.
            if (window.location.pathname === newPath) {
                onSuccess?.();
            } else {
                navigate(newPath);
            }

            // Clear the text box after successful upload.
            setText("");

            // Refresh statistics if needed.
            await refreshStatistic();

            // Build the success message.
            const successMessages: Record<string, string> = {
                candidate:
                    mode === "create"
                        ? "Candidate has been successfully created."
                        : "Candidate source has been successfully added.",
                job:
                    mode === "create"
                        ? "Job has been successfully created."
                        : "Job source has been successfully added.",
                company:
                    mode === "create"
                        ? "Company has been successfully created."
                        : "Company source has been successfully added.",
            };

            enqueueSnackbar(successMessages[entityType], {
                variant: "solid",
                state: "success",
            });
        } catch (err) {
            enqueueSnackbar(`Error: ${err}`, {
                variant: "solid",
                state: "danger",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card pb-2.5">
            <div className="card-header" id="upload_text">
                <h3 className="card-title">Copy &amp; Paste Details</h3>
            </div>
            <div className="card-body grid gap-5">
                {!loading && (
                    <div className="flex flex-col gap-4">
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Paste the text details here (e.g., job description, plain text, etc.)."
                            className="resize-none border border-gray-400 p-3 rounded-md text-sm w-full h-40 focus:outline-none"
                        />
                        <div className="flex justify-end pt-2.5">
                            <button
                                onClick={handleSubmit}
                                className="btn btn-sm btn-primary"
                                disabled={text.trim().length < 10}
                            >
                                <KeenIcon icon="plus" /> Submit Text
                            </button>
                        </div>
                    </div>
                )}

                {loading && (
                    <Loading
                        loadingText="Processing Text"
                        loaderType={LoaderType.ROBOT_READING_BOOK}
                        loading={loading}
                        delay={0}
                    />
                )}
            </div>
        </div>
    );
};

export { UploadText };
